import { useState, useEffect, useCallback, useReducer } from 'react';
import type { SortingState } from '@tanstack/react-table';

import { Box } from 'ui/box';
import { Radio } from 'ui/radio';
import { Stack } from 'ui/stack';
import { Typography } from 'ui/typography';
import { RadioGroup } from 'ui/radio-group';
import { SearchField } from 'ui/search-field';

import TableComponent from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import { usersReducer } from 'containers/admin/reducer';
import { SEARCH_CHANGE, PAGE_CHANGE } from 'containers/admin/actions';
import {
  getIncludedReportsTableSettings,
  type IncludedReportRow,
} from 'containers/admin/products/product/included-reports/included-reports-table-settings';

import {
  useAdminReportsList,
  useModifyReport,
} from 'hooks/api/use-admin-reports';

interface IncludedReportsProps {
  productId: string | number;
  userId: string;
  initialLoading: boolean;
}

interface ReducerState {
  search: string;
  pageIndex: number;
}

const initialState: ReducerState = {
  search: '',
  pageIndex: 0,
};

interface TableAmountState {
  pageSize: number;
  totalRecords: number;
}

interface RadioOption {
  label: string;
  value: string;
}

const radioOptions: RadioOption[] = [
  { label: 'Linked', value: 'linked' },
  { label: 'All available', value: 'all' },
];

const IncludedReports = ({
  productId,
  userId,
  initialLoading,
}: IncludedReportsProps): React.JSX.Element => {
  const modifyReportMutation = useModifyReport();

  const [state, dispatch] = useReducer(usersReducer, initialState);
  const { search, pageIndex } = state as ReducerState;

  const [{ pageSize }, setAmount] = useState<TableAmountState>({
    pageSize: 20,
    totalRecords: 0,
  });

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: 'title', desc: false },
  ]);

  const [filterOption, setFilterOption] = useState<string>('linked');

  const handleSearch = useCallback((payload: string): void => {
    dispatch({ type: SEARCH_CHANGE, payload });
  }, []);
  const handlePagination = useCallback((payload: number): void => {
    dispatch({ type: PAGE_CHANGE, payload });
  }, []);

  const filters = {
    title: { contains: search },
    ...(filterOption === 'linked' && { productId }),
    ...(filterOption === 'all' && { productId: { specified: 'false' } }),
  };

  const { data: reportsResponse, isFetching } = useAdminReportsList({
    pageSize,
    pageIndex,
    sortBy: sortingParams,
    filters,
  });

  const allReports = (reportsResponse?.data ?? []) as IncludedReportRow[];
  const totalRecords = reportsResponse?.pagination?.totalRecords ?? 0;

  useEffect(() => {
    dispatch({ type: PAGE_CHANGE, payload: 0 });
  }, [filterOption]);

  const handleToggleChange = async (
    report: IncludedReportRow,
    newToggleValue: boolean
  ): Promise<void> => {
    const { title, description, powerBiId } = report;

    try {
      await modifyReportMutation.mutateAsync({
        userId,
        reportId: report.id,
        reportData: {
          title: title ?? '',
          description,
          powerBiId: String(powerBiId ?? ''),
          productId: newToggleValue ? Number(productId) : undefined,
        },
      });
    } catch (error) {
      console.error('Error updating report', error);
    }
  };

  const columns = getIncludedReportsTableSettings(
    productId,
    handleToggleChange
  );

  const isBusy = isFetching || modifyReportMutation.isPending;

  const renderContent = (): React.JSX.Element | null => {
    if (allReports.length > 0 || (isBusy && !initialLoading)) {
      return (
        <TableComponent
          columns={columns}
          isLoading={isBusy}
          data={allReports}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPagination={handlePagination}
          setAmount={setAmount}
          sortingParams={sortingParams}
          setSortingParams={setSortingParams}
          totalRecords={totalRecords}
          customHeight="max-h-[50vh]"
        />
      );
    }

    if (!isBusy && !initialLoading && allReports.length === 0) {
      return (
        <EmptySpaceContent
          text="There are no reports to show."
          customClass="!mt-14 !h-[350px]"
        />
      );
    }

    return null;
  };

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ my: 2 }}>
          <SearchField onSearch={handleSearch} />
        </Box>
        <Stack
          direction="row"
          sx={{ gap: 2.5, position: 'absolute', zIndex: 10 }}
        >
          <Typography id="included-reports-show-label" variant="subtitle1">
            Show
          </Typography>
          <RadioGroup
            aria-labelledby="included-reports-show-label"
            row
            size="small"
            value={filterOption}
            onChange={(_event, value) => setFilterOption(value)}
          >
            {radioOptions.map((option) => (
              <Radio
                key={option.value}
                value={option.value}
                label={option.label}
                id={option.value}
              />
            ))}
          </RadioGroup>
        </Stack>
      </Box>

      <Box sx={{ position: 'relative', mt: 2 }}>{renderContent()}</Box>
    </>
  );
};

export default IncludedReports;
